import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, CloudOff, Loader2, Mail, Smartphone } from 'lucide-react';
import type { EntryBrand, FormAnswers, FormIssue, FormPublicFillState, FormSubmitResult, FormUploadInfo } from '@kaba/shared';
import { api, ApiError, newOpId } from '../../lib/api';
import { Button } from '../ui/ui';
import { FormRenderer, type UploadApi } from './FormRenderer';
import { useSerialSave } from '../../lib/serialSave';
import { classifySave, DraftSaveStatus, Shell, uploadWithProgress } from './FormFillPage';

type Saved = { revision: number; expiresAt: string };

const fieldCls = 'min-h-11 w-full rounded-lg border border-[var(--line)] bg-[var(--surface)] px-3 text-base';

/**
 * Öffentliches Formular ohne Konto: /formular/:token (0.31).
 * Sitzung über ein HttpOnly-Cookie; keine Board- oder Kartenangaben, kein Link ins Board.
 */
export function PublicFormPage({ token }: { token: string }) {
  const base = `/api/public/forms/${token}`;
  const [state, setState] = useState<FormPublicFillState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<FormAnswers>({});
  const [uploads, setUploads] = useState<FormUploadInfo[]>([]);
  const [until, setUntil] = useState<string | null>(null);
  const [done, setDone] = useState<FormSubmitResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverIssues, setServerIssues] = useState<FormIssue[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<string | null>(null);
  const [challengeAnswer, setChallengeAnswer] = useState('');
  const [trap, setTrap] = useState('');
  const conflictRev = useRef<number | null>(null);
  const { saver, state: save, setState: setSave } = useSerialSave<FormAnswers, Saved>({
    send: (a, revision) => api.put<Saved>(`${base}/session`, { answers: a, revision }),
    classify: (e) => classifySave(e, conflictRev),
  });
  const latest = useRef<FormAnswers>({});
  const attempt = useRef(newOpId());
  const challengeRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      // Fortsetzungslink nur einmal verwenden und danach aus der Adresszeile entfernen
      const resume = new URLSearchParams(window.location.search).get('fortsetzen');
      const s = await api.get<FormPublicFillState>(`${base}${resume ? `?fortsetzen=${encodeURIComponent(resume)}` : ''}`);
      if (resume) window.history.replaceState(null, '', window.location.pathname);
      setState(s);
      if (s.session) {
        setAnswers(s.session.answers);
        latest.current = s.session.answers;
        setUploads(s.session.uploads);
        saver.reset(s.session.answers, s.session.revision);
        saver.open();
        setUntil(s.session.expiresAt);
        setSave({ kind: 'idle' });
      }
    } catch (e) {
      setError(e instanceof ApiError && e.status === 404 ? 'Dieses Formular gibt es nicht (mehr). Vielleicht wurde der Link ersetzt – frag bei der Stelle nach, von der du ihn hast.' : (e as Error).message);
    }
  }, [base, saver, setSave]);
  useEffect(() => {
    void load();
  }, [load]);

  const onChange = (a: FormAnswers) => {
    setAnswers(a);
    latest.current = a;
    setServerIssues([]);
    saver.change(a);
  };
  const flush = () => saver.flush();
  const loadOther = () => {
    if (window.confirm('Den anderen gespeicherten Stand laden? Deine Eingaben hier seit dem letzten Speichern gehen dabei verloren.')) void load();
  };

  const uploadApi: UploadApi = {
    list: uploads,
    upload: async (key, file, onProgress) => {
      const fd = new FormData();
      fd.set('file', file);
      fd.set('key', key);
      const u = await uploadWithProgress(`${base}/uploads`, fd, onProgress);
      setUploads((x) => [...x, u]);
      return u;
    },
    remove: async (id) => {
      await api.del(`${base}/uploads/${id}`);
      setUploads((x) => x.filter((u) => u.id !== id));
    },
    url: (id, thumb) => `${base}/uploads/${id}${thumb ? '?thumb=1' : ''}`,
  };

  const send = async (withChallenge?: string) => {
    setSubmitting(true);
    setSubmitError(null);
    saver.close();
    let ok = false;
    try {
      const r = await api.post<FormSubmitResult>(`${base}/submit`, {
        answers: latest.current,
        attempt: attempt.current,
        website: trap || undefined,
        ...(withChallenge ? { challenge: withChallenge } : {}),
      });
      ok = true;
      setChallenge(null);
      setDone(r);
      window.scrollTo({ top: 0 });
    } catch (e) {
      const d = e instanceof ApiError ? (e.data as { challenge?: string; details?: { issues?: FormIssue[] } } | undefined) : undefined;
      if (e instanceof ApiError && e.status === 428 && d?.challenge) {
        setChallenge(d.challenge);
        setChallengeAnswer('');
        if (withChallenge) setSubmitError('Die Antwort stimmt nicht. Hier ist eine neue Frage.');
        requestAnimationFrame(() => challengeRef.current?.focus());
        return;
      }
      if (d?.details?.issues?.length) setServerIssues(d.details.issues);
      setSubmitError(
        e instanceof ApiError && e.status === 0
          ? 'Keine Verbindung. Deine Angaben bleiben erhalten – bitte erneut absenden.'
          : e instanceof ApiError && e.status === 410
            ? 'Deine Sitzung ist abgelaufen oder der Link wurde ersetzt. Bitte lade das Formular neu.'
            : (e as Error).message,
      );
    } finally {
      setSubmitting(false);
      if (!ok) {
        saver.open();
        void saver.flush();
      }
    }
  };

  if (error)
    return (
      <Shell title="Formular">
        <p role="alert" className="rounded-xl bg-rose-50 p-4 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-100">
          {error}
        </p>
      </Shell>
    );
  if (!state)
    return (
      <Shell title="Formular">
        <p className="text-sm text-[var(--ink-soft)]">Lade …</p>
      </Shell>
    );
  if (done) return <PublicDone base={base} title={state.title} done={done} mailEnabled={state.mailEnabled} brand={state.brand} />;
  if (state.closed || !state.definition || !state.session)
    return (
      <Shell title={state.title} brand={state.brand}>
        <p className="rounded-xl bg-[var(--column)] p-4 text-sm">{state.closed ?? 'Dieses Formular ist gerade nicht verfügbar.'}</p>
      </Shell>
    );

  return (
    <Shell title={state.definition.title} brand={state.brand}>
      {state.definition.intro && <p className="text-sm whitespace-pre-line">{state.definition.intro}</p>}
      {state.resumed && (
        <p role="status" className="rounded-lg bg-sky-50 p-3 text-sm text-sky-900 dark:bg-sky-950 dark:text-sky-100">
          Dein Entwurf ist da. Du kannst hier weitermachen.
        </p>
      )}
      {/* Fangfeld: für Menschen unsichtbar und nicht erreichbar */}
      <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
        <label>
          Webseite
          <input tabIndex={-1} autoComplete="off" name="website" value={trap} onChange={(e) => setTrap(e.target.value)} />
        </label>
      </div>
      <FormRenderer def={state.definition} answers={answers} onChange={onChange} uploads={uploadApi} submitting={submitting} serverIssues={serverIssues} onSubmit={() => send()} />
      {challenge && (
        <form
          className="flex flex-col gap-2 rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950"
          onSubmit={(e) => {
            e.preventDefault();
            if (challengeAnswer.trim()) void send(challengeAnswer.trim());
          }}
        >
          <label htmlFor="kaba-challenge" className="text-sm font-medium">
            Zur Sicherheit noch eine kurze Frage: {challenge}
          </label>
          <div className="flex gap-2">
            <input
              ref={challengeRef}
              id="kaba-challenge"
              inputMode="numeric"
              autoComplete="off"
              className={`${fieldCls} max-w-32`}
              value={challengeAnswer}
              onChange={(e) => setChallengeAnswer(e.target.value)}
            />
            <Button variant="primary" type="submit" disabled={submitting || !challengeAnswer.trim()}>
              Absenden
            </Button>
          </div>
        </form>
      )}
      {submitError && (
        <p role="alert" className="rounded-lg bg-rose-50 p-3 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-100">
          {submitError}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-[var(--ink-soft)]">
        <span aria-live="polite" className="inline-flex items-center gap-1.5">
          <DraftSaveStatus state={save} until={until} deviceNote onKeepMine={() => void saver.overwrite(conflictRev.current ?? saver.revision)} onLoadOther={loadOther} onReload={() => void load()} />
        </span>
        <button
          className="min-h-10 underline"
          onClick={async () => {
            if (!window.confirm('Entwurf verwerfen? Deine Antworten und hochgeladenen Dateien werden gelöscht.')) return;
            saver.close();
            await api.del(`${base}/session`).catch(() => undefined);
            void load();
          }}
        >
          Entwurf verwerfen
        </button>
      </div>
      {state.mailEnabled && <ResumeByMail base={base} beforeSend={flush} />}
    </Shell>
  );
}

/** Auf einem anderen Gerät fortsetzen: Link per E-Mail, 24 Stunden gültig (E01) */
function ResumeByMail({ base, beforeSend }: { base: string; beforeSend: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  if (!open)
    return (
      <button className="inline-flex min-h-10 items-center gap-1.5 self-start text-sm underline" onClick={() => setOpen(true)}>
        <Smartphone size={14} aria-hidden /> Auf einem anderen Gerät fortsetzen
      </button>
    );
  return (
    <form
      className="flex flex-col gap-2 rounded-xl bg-[var(--column)] p-3"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setMsg(null);
        try {
          await beforeSend();
          await api.post(`${base}/resume`, { email });
          setMsg({ ok: true, text: 'Wir haben dir einen Link geschickt. Er gilt 24 Stunden und funktioniert einmal.' });
        } catch (err) {
          setMsg({ ok: false, text: (err as Error).message });
        } finally {
          setBusy(false);
        }
      }}
    >
      <label htmlFor="kaba-resume" className="text-sm font-medium">
        E-Mail-Adresse für den Fortsetzungslink
      </label>
      <p className="text-xs text-[var(--ink-soft)]">Deine Antworten stehen nicht in der E-Mail. Wir speichern die Adresse nicht.</p>
      <div className="flex flex-wrap gap-2">
        <input id="kaba-resume" type="email" required autoComplete="email" className={`${fieldCls} max-w-72 flex-1`} value={email} onChange={(e) => setEmail(e.target.value)} />
        <Button type="submit" disabled={busy}>
          Link senden
        </Button>
      </div>
      {msg && (
        <p role={msg.ok ? 'status' : 'alert'} className={`text-sm ${msg.ok ? '' : 'text-rose-700 dark:text-rose-300'}`}>
          {msg.text}
        </p>
      )}
    </form>
  );
}

function PublicDone({ base, title, done, mailEnabled, brand }: { base: string; title: string; done: FormSubmitResult; mailEnabled: boolean; brand?: EntryBrand | null }) {
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <Shell title={title} brand={brand}>
      <div className="flex flex-col items-start gap-3 rounded-2xl border border-emerald-300 bg-emerald-50 p-5 dark:border-emerald-800 dark:bg-emerald-950" role="status">
        <CheckCircle2 size={32} className="text-emerald-600" aria-hidden />
        <p className="text-lg font-semibold">{done.confirmation}</p>
        <p className="text-sm">
          Eingangsnummer: <strong className="tabular-nums">{done.number}</strong>
        </p>
        {done.expectation && <p className="text-sm">{done.expectation}</p>}
        <p className="text-xs text-[var(--ink-soft)]">Notiere dir die Eingangsnummer, wenn du später nachfragen möchtest.</p>
      </div>
      {mailEnabled && (
        <form
          className="flex flex-col gap-2"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setMsg(null);
            try {
              await api.post(`${base}/confirmation`, { email });
              setMsg({ ok: true, text: 'Bitte bestätige deine Adresse über den Link in der E-Mail. Danach schicken wir die Eingangsbestätigung.' });
            } catch (err) {
              setMsg({ ok: false, text: (err as Error).message });
            } finally {
              setBusy(false);
            }
          }}
        >
          <label htmlFor="kaba-confirm" className="inline-flex items-center gap-1.5 text-sm font-medium">
            <Mail size={14} aria-hidden /> Bestätigung per E-Mail (optional)
          </label>
          <p className="text-xs text-[var(--ink-soft)]">Die Bestätigung enthält nur Eingangsnummer und Formularname, keine Inhalte.</p>
          <div className="flex flex-wrap gap-2">
            <input id="kaba-confirm" type="email" required autoComplete="email" className={`${fieldCls} max-w-72 flex-1`} value={email} onChange={(e) => setEmail(e.target.value)} />
            <Button type="submit" disabled={busy}>
              Senden
            </Button>
          </div>
          {msg && (
            <p role={msg.ok ? 'status' : 'alert'} className={`text-sm ${msg.ok ? '' : 'text-rose-700 dark:text-rose-300'}`}>
              {msg.text}
            </p>
          )}
        </form>
      )}
    </Shell>
  );
}

/** Ziel des Prüflinks aus der Bestätigungsmail */
export function FormConfirmedPage() {
  const ok = new URLSearchParams(window.location.search).get('ok') === '1';
  return (
    <Shell title={ok ? 'Adresse bestätigt' : 'Link ungültig'}>
      <p className="text-sm" role="status">
        {ok ? 'Danke! Die Eingangsbestätigung ist unterwegs.' : 'Dieser Bestätigungslink ist ungültig oder abgelaufen.'}
      </p>
    </Shell>
  );
}
